import { createColumnHelper } from "@tanstack/react-table";
import {
  primaryDueBadge,
  type DueFilter,
  type PayAxisFilter,
  type ReceiveAxisFilter,
} from "@icraftx/shared/v1";
import { AxisProgress } from "@/modules/core/components/AxisProgress";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import {
  DueBadge,
  FilterPartyName,
} from "@/modules/core/components/DueBadge";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import {
  dueBadgeToFilter,
  toggleFilter,
  togglePartyFilter,
  type PartyFilter,
} from "@/modules/core/list-cell-actions";
import {
  type DeskTableFeatures,
  useDataTableShowRelativeTime,
} from "@/modules/shell/data-table";
import { DeskDocListRowMenu } from "@/modules/shell/DeskDocListRowMenu";
import {
  docDateRelativeLabel,
  formatDocDateLabel,
} from "@/modules/shell/relative-time";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { shouldFlashAfterToggle } from "@/modules/shell/use-list-row-flash";
import { AssigneeAvatars } from "@/modules/tags/components/AssigneeAvatars";
import { ListTagPills } from "@/modules/tags/components/TagPill";
import type { PoListRow } from "@/shared/api/purchasing";
import type { AssigneeRow } from "@/shared/api/tags";

export type PoTableRow = PoListRow & {
  tags: { id: string; name: string; color?: string | null }[];
  assignees: AssigneeRow[];
};

export type PoListCellActions = {
  partyFilter: PartyFilter | null;
  setPartyFilter: (next: PartyFilter | null) => void;
  payFilter: PayAxisFilter;
  setPayFilter: (next: PayAxisFilter) => void;
  receiveFilter: ReceiveAxisFilter;
  setReceiveFilter: (next: ReceiveAxisFilter) => void;
  dueFilter: DueFilter;
  setDueFilter: (next: DueFilter) => void;
  tagId: string;
  setTagId: (next: string) => void;
  onFilterFromRow?: (docId: string) => void;
  listPath: string;
};

const columnHelper = createColumnHelper<DeskTableFeatures, PoTableRow>();

function DocDateCell(props: { raw: string }) {
  const showRelative = useDataTableShowRelativeTime();
  const relative = showRelative ? docDateRelativeLabel(props.raw) : "";
  return (
    <span className="flex flex-col tabular-nums">
      <span className="text-sm">{formatDocDateLabel(props.raw)}</span>
      {relative ? (
        <span className="text-xs text-[var(--color-muted)]">{relative}</span>
      ) : null}
    </span>
  );
}

function flashIfSet(
  actions: PoListCellActions,
  docId: string,
  didSet: boolean,
) {
  if (didSet) actions.onFilterFromRow?.(docId);
}

export function purchaseOrderColumns(
  companySlug: string,
  actions: PoListCellActions,
) {
  return columnHelper.columns([
    columnHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 120,
      cell: (info) => {
        const row = info.row.original;
        const docTo = `/c/${companySlug}/purchasing/orders/${row.id}`;
        return (
          <DocNumberLink
            to={docTo}
            onClick={() => stashListReturn(actions.listPath, row.id)}
          >
            {info.getValue()}
          </DocNumberLink>
        );
      },
    }),
    columnHelper.accessor("supplierName", {
      id: "supplierName",
      header: "Supplier",
      size: 180,
      cell: (info) => {
        const row = info.row.original;
        return (
          <FilterPartyName
            name={info.getValue()}
            onClick={() => {
              const next = togglePartyFilter(actions.partyFilter, {
                id: row.supplierId,
                name: row.supplierName,
              });
              actions.setPartyFilter(next);
              flashIfSet(actions, row.id, next != null);
            }}
          />
        );
      },
    }),
    columnHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/purchasing/orders/${info.row.original.id}`}
        />
      ),
    }),
    columnHelper.display({
      id: "payPct",
      header: "Paid",
      size: 130,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <AxisProgress
            axis="Paid"
            format="money"
            current={r.amountPaid}
            max={r.total}
            onFilterClick={() => {
              const next = toggleFilter(
                actions.payFilter,
                r.pay as PayAxisFilter,
                "all",
              );
              actions.setPayFilter(next);
              flashIfSet(
                actions,
                r.id,
                shouldFlashAfterToggle(next, "all"),
              );
            }}
          />
        );
      },
    }),
    columnHelper.display({
      id: "receivePct",
      header: "Received",
      size: 110,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <AxisProgress
            axis="Received"
            format="qty"
            current={r.qtyReceived}
            max={r.qtyOrdered}
            onFilterClick={() => {
              const next = toggleFilter(
                actions.receiveFilter,
                r.receive as ReceiveAxisFilter,
                "all",
              );
              actions.setReceiveFilter(next);
              flashIfSet(
                actions,
                r.id,
                shouldFlashAfterToggle(next, "all"),
              );
            }}
          />
        );
      },
    }),
    columnHelper.accessor("total", {
      id: "total",
      header: "Total",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">{info.getValue()}</span>
      ),
    }),
    columnHelper.display({
      id: "dueAt",
      header: "Due",
      size: 120,
      enableSorting: true,
      cell: ({ row }) => {
        const due = primaryDueBadge({
          paymentDueOn: row.original.paymentDueOn,
          expectedOn: row.original.expectedOn,
        });
        if (!due.label) return "—";
        const filter = dueBadgeToFilter(due);
        return (
          <DueBadge
            badge={due}
            onFilterClick={
              filter
                ? () => {
                    const next = toggleFilter(
                      actions.dueFilter,
                      filter,
                      "all",
                    );
                    actions.setDueFilter(next);
                    flashIfSet(
                      actions,
                      row.original.id,
                      shouldFlashAfterToggle(next, "all"),
                    );
                  }
                : undefined
            }
          />
        );
      },
    }),
    columnHelper.display({
      id: "assignees",
      header: "People",
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <AssigneeAvatars assignees={row.original.assignees} />
      ),
    }),
    columnHelper.display({
      id: "tags",
      header: "Tags",
      size: 140,
      enableSorting: false,
      cell: ({ row }) => (
        <ListTagPills
          tags={row.original.tags}
          activeTagId={actions.tagId || undefined}
          onFilterTag={(tagId) => {
            const next = toggleFilter(actions.tagId, tagId, "");
            actions.setTagId(next);
            flashIfSet(
              actions,
              row.original.id,
              shouldFlashAfterToggle(next, ""),
            );
          }}
        />
      ),
    }),
    columnHelper.accessor("docDate", {
      id: "docDate",
      header: "Date",
      size: 100,
      enableSorting: true,
      cell: (info) => {
        const raw = info.getValue();
        if (!raw) return "—";
        return <DocDateCell raw={raw} />;
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 48,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const docPath = `/c/${companySlug}/purchasing/orders/${row.original.id}`;
        return (
          <DeskDocListRowMenu
            docPath={docPath}
            assignPath={docPath}
            tabLinks={[
              {
                label: "Receive",
                to: `${docPath}?tab=receipts`,
              },
              {
                label: "Pay",
                to: `${docPath}?tab=payments`,
              },
            ]}
          />
        );
      },
    }),
  ]);
}
